import { useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText, FileJson, Plus, Trash2, Calendar } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { ASSET_TYPES } from "@/lib/format";

const FREQUENCIES = [
  { value: "hourly", label: "Hourly" },
  { value: "every_4h", label: "Every 4 Hours" },
  { value: "every_8h", label: "Every 8 Hours" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export default function Reports() {
  const [overview, setOverview] = useState(null);
  const [scope, setScope] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [showSched, setShowSched] = useState(false);
  const [trend, setTrend] = useState(null);

  const loadOverview = () => api.get("/dashboard/overview").then((r) => setOverview(r.data));
  const loadSchedules = () => api.get("/reports/schedule").then((r) => setSchedules(r.data));
  const loadTrend = () => api.get("/dashboard/outreach-conversion-trend").then((r) => setTrend(r.data)).catch(() => setTrend({ trend: [], benchmark: 8.5, period_days: 30 }));

  useEffect(() => { loadOverview(); loadSchedules(); loadTrend(); }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/export/opportunities.csv", {
        params: scope ? { asset_type: scope } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `opportunities-${scope || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Failed"); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/export/executive.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `executive-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
  };

  const deleteSched = async (sid) => {
    await api.delete(`/reports/schedule/${sid}`);
    toast.success("Schedule removed");
    loadSchedules();
  };

  if (!overview) return <div className="text-slate-500">Loading…</div>;
  const t = overview.totals;

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Module 7</div>
        <h1 className="font-heading text-4xl tracking-tight">Reports & Exports</h1>
        <p className="text-sm text-slate-400 mt-1">Generate executive summaries, exports, and scheduled deliveries</p>
      </header>

      {/* Executive summary */}
      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-3">Executive Summary</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Opportunities" value={t.total} />
          <Stat label="In Pipeline" value={t.in_pipeline} />
          <Stat label="Seller Financing" value={t.seller_financing} />
          <Stat label="Master Lease" value={t.master_lease} />
          <Stat label="Creative Financing" value={t.creative_financing} />
          <Stat label="Distress" value={t.distress} />
          <Stat label="Businesses" value={t.business} />
          <Stat label="Multifamily + Land" value={t.multifamily + t.land} />
        </div>
      </div>

      {/* Outreach → Engaged rate trend */}
      <div className="ae-card p-6" data-testid="outreach-conversion-trend-card">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Outreach → Engaged Rate Trend</div>
            <p className="text-xs text-slate-500 mt-1">Daily reply rate for auto-fired sequences (last {trend?.period_days ?? 30} days)</p>
          </div>
          <div className="text-xs text-slate-400">Industry avg: <span className="font-mono text-slate-200">{trend?.benchmark ?? 8.5}%</span></div>
        </div>
        {trend && trend.trend && trend.trend.length > 0 ? (
          <div className="h-64" data-testid="outreach-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.trend} margin={{ top: 10, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="2 3" vertical={false} />
                <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748b" fontSize={10} unit="%" domain={[0, 'dataMax + 5']} />
                <Tooltip
                  contentStyle={{ background: "#0A0D12", border: "1px solid #1e293b", borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: "#94a3b8" }}
                  formatter={(v, name) => name === "rate" ? [`${v}%`, "Engaged Rate"] : [v, name]}
                />
                <ReferenceLine y={trend.benchmark} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "Benchmark", fill: "#f59e0b", fontSize: 10, position: "insideTopRight" }} />
                <Line type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="outreach-trend-empty">
            No auto-outreach data yet. Trend will appear once sequences fire.
          </div>
        )}
      </div>

      {/* Export */}
      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-3">Quick Exports</div>
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs text-slate-400">Asset scope:</label>
          <select data-testid="export-scope" value={scope} onChange={(e) => setScope(e.target.value)} className="bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-sm">
            <option value="">All assets</option>
            {ASSET_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ExportCard icon={FileSpreadsheet} label="CSV Export" sub="Excel & Google Sheets compatible" onClick={downloadCsv} testid="export-csv" />
          <ExportCard icon={FileText} label="PDF Executive Report" sub="Branded summary with top picks" onClick={downloadPdf} testid="export-pdf" />
          <ExportCard icon={FileJson} label="GraphQL API" sub="POST /api/graphql, query opportunities and leads" onClick={() => toast.info("GraphQL endpoint: /api/graphql")} testid="export-api" />
        </div>
      </div>

      {/* Scheduled reports */}
      <div className="ae-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Scheduled Reports</div>
            <p className="text-xs text-slate-500 mt-1">Receive executive summaries via email automatically</p>
          </div>
          <button onClick={() => setShowSched(true)} data-testid="new-schedule-btn" className="btn-primary text-sm inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> New schedule
          </button>
        </div>

        {schedules.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">
            <Calendar className="h-8 w-8 mx-auto text-slate-700 mb-2" /> No scheduled reports yet
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {schedules.map((s) => (
              <div key={s.sid} data-testid={`sched-row-${s.sid}`} className="py-3 flex items-center gap-3">
                <Calendar className="h-4 w-4 text-emerald-400" />
                <div className="flex-1">
                  <div className="text-sm">
                    <span className="font-medium capitalize">{s.frequency.replace("_", " ")}</span> · <span className="text-slate-400">{s.email_to}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {s.last_run_at ? `Last run: ${fmtDateTime(s.last_run_at)}` : "Pending first run"}
                  </div>
                </div>
                <button onClick={() => deleteSched(s.sid)} data-testid={`sched-delete-${s.sid}`} className="text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-sm">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showSched && <ScheduleDialog onClose={() => setShowSched(false)} onCreated={() => { setShowSched(false); loadSchedules(); }} />}
    </div>
  );
}

function ScheduleDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ frequency: "daily", email_to: "", format: "pdf", asset_scope: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reports/schedule", {
        ...f,
        email_to: f.email_to || null,
        asset_scope: f.asset_scope || null,
      });
      toast.success("Schedule created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
      <form onSubmit={submit} className="ae-card p-6 max-w-md w-full" data-testid="sched-dialog">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400">New Schedule</div>
        <h2 className="font-heading text-2xl tracking-tight mt-1">Recurring email report</h2>
        <div className="space-y-3 mt-4 text-sm">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Frequency</label>
            <select data-testid="sched-freq" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} className="inp">
              {FREQUENCIES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Send to (leave blank for your email)</label>
            <input data-testid="sched-email" type="email" value={f.email_to} onChange={(e) => setF({ ...f, email_to: e.target.value })} placeholder="ceo@company.com" className="inp" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="sched-submit" className="btn-primary text-sm">{busy ? "Creating…" : "Create"}</button>
        </div>
        <style>{`.inp { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.5rem 0.625rem; font-size: 0.875rem; color: #F8FAFC; outline: none; }`}</style>
      </form>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A0D12] border border-white/8 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function ExportCard({ icon: I, label, sub, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="ae-card ae-card-hover p-5 text-left disabled:opacity-50 disabled:cursor-not-allowed flex items-start gap-3"
    >
      <div className="h-9 w-9 rounded-sm bg-emerald-500/10 flex items-center justify-center shrink-0">
        <I className="h-4 w-4 text-emerald-400" />
      </div>
      <div>
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-slate-500 mt-1">{sub}</div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mt-2 inline-flex items-center gap-1">
          <Download className="h-3 w-3" /> Download
        </div>
      </div>
    </button>
  );
}
